<template>
  <div class="mobile-contact-container">
    <!-- Banner Section -->
    <section class="relative h-64 overflow-hidden">
      <video 
        :src="aboutus" 
        autoplay 
        muted 
        playsinline 
        loop 
        class="w-full h-full object-cover block">
      </video>
      <div class="absolute inset-0 bg-black bg-opacity-40"></div>
      <div class="absolute inset-0 flex items-center px-4">
        <div>
          <h1 class="text-white text-[6vw] font-bold mb-2">{{ contactTexts.title }}</h1>
          <p class="text-white text-[3.5vw] leading-[1.4]">{{ contactTexts.subtitle }}</p>
        </div>
      </div>
    </section>

    <!-- Contact Form -->
    <section class="bg-[#DEE8F7] mx-4 -mt-16 relative z-10 rounded-lg p-6">
      <form ref="formRef" @submit.prevent="submitForm" novalidate class="space-y-4">
        <!-- Name Field -->
        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-2">
            {{ contactTexts.name }}<span class="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            v-model="formData.firstName" 
            @focus="onFormFocus"
            :placeholder="contactTexts.namePlaceholder" 
            @blur="onBlur('firstName', formData.firstName)"
            class="w-full rounded-md border-0 bg-white p-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600" 
          />
        </div>

        <!-- Email Field -->
        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-2">
            {{ contactTexts.email }}<span class="text-red-500">*</span>
          </label>
          <input
            type="email"
            v-model="formData.email"
            @focus="onFormFocus"
            :placeholder="contactTexts.emailPlaceholder"
            @blur="onBlur('email', formData.email)"
            class="w-full rounded-md border-0 bg-white p-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
          />
        </div>

        <!-- Company Field -->
        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-2">
            {{ contactTexts.company }}<span class="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            v-model="formData.companyName" 
            @focus="onFormFocus"
            :placeholder="contactTexts.companyPlaceholder" 
            @blur="onBlur('companyName', formData.companyName)"
            class="w-full rounded-md border-0 bg-white p-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600" 
          />
        </div>

        <!-- Message Field -->
        <div>
          <label class="block text-sm font-semibold text-gray-800 mb-2">
            {{ contactTexts.message }}<span class="text-red-500">*</span>
          </label>
          <textarea 
            v-model="formData.lookingFor"
            @focus="onFormFocus" 
            @blur="onBlur('lookingFor', formData.lookingFor)" 
            :placeholder="contactTexts.messagePlaceholder" 
            rows="4"
            class="w-full rounded-md border-0 bg-white p-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
          ></textarea>
        </div>

        <!-- reCAPTCHA -->
        <div class="w-full">
          <div id="recaptcha-container-mobile"></div>
        </div>

        <!-- Submit Button -->
        <button 
          type="submit" 
          class="w-full px-4 py-3 text-lg font-bold text-white bg-[#0D0971] rounded"
          :style="buttonStyle" 
          @mouseover="hovered = true" 
          @mouseleave="hovered = false"
        >
          {{ contactTexts.submit }}
        </button>
      </form>
    </section>

    <!-- Contact Info -->
    <section class="pt-2 pb-0">
      <div class="flex h-64">
        <!-- 地图区域 - 左侧50% -->
        <div class="w-1/2">
          <div ref="mapContainer" class="w-full h-full"></div>
        </div>
        
        <!-- 联系信息区域 - 右侧50% -->
        <div class="w-1/2">
          <div class="bg-[#FFA200] p-4 text-black h-full flex flex-col justify-center">
            <h2 class="text-sm font-bold mb-2">{{ contactTexts.headquarters }}</h2>
            <p class="text-xs font-medium mb-2 cursor-pointer hover:underline" @click="openGoogleMap">
              {{ contactTexts.address }}
            </p>
            <div class="flex items-center text-xs font-medium">
              <svg class="w-3 h-3 mr-1 fill-current" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <a href="mailto:ailsalin@creston-group.com" class="cursor-pointer hover:underline text-black">
                ailsajin@creston-group.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import aboutus from "@/assets/aboutus.mp4";
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { getAnonymousId } from "../utils/anonymous";
import { getUtmParams, getFirstPageUrl, getLandingPage } from "../utils/utmParams";
import { ElMessage } from "element-plus";
import { API_BASE_URL } from "../config/config";
import { pushToDataLayer } from '../utils/gtm';
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { getCTAInfo, clearCTAInfo } from '../utils/ctaTracking';
import { getFormSubmitSuccessParams, getDiagnosticEventParams } from '../utils/leadTracking';

const router = useRouter();

const { locale, messages } = useI18n();

// 获取联系我们的文本
const contactTexts = computed(() => {
  return messages.value[locale.value]?.contactUs || {
    title: 'Contact Us',
    subtitle: 'Get in touch with us',
    name: 'Name',
    email: 'Email',
    company: 'Company',
    message: 'Message',
    submit: 'Submit',
    headquarters: 'Headquarters',
    address: 'Address',
    namePlaceholder: 'Enter your name',
    emailPlaceholder: 'Enter your email',
    companyPlaceholder: 'Enter your company',
    messagePlaceholder: 'Enter your message',
    validationMessages: {
      nameRequired: 'Name is required',
      emailRequired: 'Email is required',
      companyRequired: 'Company is required',
      messageRequired: 'Message is required',
      invalidEmail: 'Invalid email format',
      captchaRequired: 'Please complete the captcha',
      submitSuccess: 'Form submitted successfully',
      submitError: 'Submission failed'
    }
  };
});

// reCAPTCHA 相关
const recaptchaToken = ref('');
const siteKey = ref("6LcSGXUrAAAAAJIyf_G5grJzDCN1yWMv2IXfhcSt");

// reCAPTCHA 回调函数
const onCaptchaVerified = (token: string) => {
  recaptchaToken.value = token;
};

const onCaptchaExpired = () => {
  recaptchaToken.value = '';
};

useHead({
  title: 'Contact Us | Creston Group – Engineering, Logistics & Sourcing from China',
  meta: [
    {
      name: 'description',
      content: 'Contact Creston Group in Shanghai for inquiries about engineering, logistics, sourcing, sales and distribution. Our team is ready to assist clients worldwide.'
    }
  ]
});

// 地图相关
const mapContainer = ref<HTMLElement | null>(null);

// 表单 ref 用于 form_view 追踪
const formRef = ref<HTMLElement | null>(null);
let formViewObserver: IntersectionObserver | null = null;
let formViewFired = false;

// 保存 interval ID 用于清理
let recaptchaInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  // 检查 window 对象是否存在（SSR 兼容性）
  if (typeof window === 'undefined') {
    return;
  }

  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });

  // 设置 form_view 的 Intersection Observer
  // 当表单进入视口 ≥50% 且持续 ≥1秒时触发
  if (formRef.value && 'IntersectionObserver' in window) {
    let visibilityTimer: ReturnType<typeof setTimeout> | null = null;

    formViewObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // 开始计时，1秒后触发 form_view
          if (!visibilityTimer && !formViewFired) {
            visibilityTimer = setTimeout(() => {
              if (!formViewFired) {
                formViewFired = true;
                // 获取诊断事件参数（包含双归因和 UTM）
                const diagnosticParams = getDiagnosticEventParams();
                pushToDataLayer('form_view', {
                  form_id: 'contact_us_form_mobile',
                  form_name: 'Contact Us Mobile',
                  ...diagnosticParams,
                });
                // 不再需要观察
                if (formViewObserver) {
                  formViewObserver.disconnect();
                }
              }
            }, 1000);
          }
        } else {
          // 离开视口，取消计时
          if (visibilityTimer) {
            clearTimeout(visibilityTimer);
            visibilityTimer = null;
          }
        }
      });
    }, {
      threshold: 0.5 // 50% 可见
    });

    formViewObserver.observe(formRef.value);
  }

  // 注册回调
  if (window) {
    (window as any).onCaptchaVerified = onCaptchaVerified;
    (window as any).onCaptchaExpired = onCaptchaExpired;
  }

  // 渲染 reCAPTCHA - 添加 DOM 检查和更好的错误处理
  const renderRecaptcha = () => {
    const container = document.getElementById("recaptcha-container-mobile");
    if (!container) return;

    const grecaptcha = (window as any).grecaptcha;
    if (grecaptcha && grecaptcha.render) {
      try {
        grecaptcha.render("recaptcha-container-mobile", {
          sitekey: siteKey.value,
          callback: onCaptchaVerified,
          "expired-callback": onCaptchaExpired,
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  };

  // 等待 reCAPTCHA 加载
  let attempts = 0;
  const maxAttempts = 50; // 5 秒超时
  recaptchaInterval = setInterval(() => {
    attempts++;
    const grecaptcha = (window as any).grecaptcha;
    if (grecaptcha && grecaptcha.render) {
      if (recaptchaInterval) clearInterval(recaptchaInterval);
      recaptchaInterval = null;
      renderRecaptcha();
    } else if (attempts >= maxAttempts) {
      if (recaptchaInterval) clearInterval(recaptchaInterval);
      recaptchaInterval = null;
      console.warn('reCAPTCHA failed to load after 5 seconds');
    }
  }, 100);

  // 地图初始化
  const initMap = () => {
    if (mapContainer.value) {
      const map = new google.maps.Map(mapContainer.value, {
        center: { lat: 31.07060136083483, lng: 121.39264145767085 },
        zoom: 14,
        mapTypeControl: false,
        language: locale.value,
        region: locale.value === 'vi' ? 'VN' : 'US',
      });

      const marker = new google.maps.Marker({
        position: { lat: 31.07060136083483, lng: 121.39264145767085 },
        map,
        title: 'Creston Group China Headquarters',
      });

      marker.addListener('click', openGoogleMap);
    }
  };

  if (window.google && window.google.maps) {
    initMap();
  } else {
    (window as any).initMap = initMap;
  }
});

// 组件卸载时清理
onBeforeUnmount(() => {
  // 清理 reCAPTCHA interval
  if (recaptchaInterval) {
    clearInterval(recaptchaInterval);
    recaptchaInterval = null;
  }

  // 清理 form_view observer
  if (formViewObserver) {
    formViewObserver.disconnect();
    formViewObserver = null;
  }

  // 清理全局回调函数
  if (typeof window !== 'undefined') {
    delete (window as any).onCaptchaVerified;
    delete (window as any).onCaptchaExpired;
    delete (window as any).initMap;
  }
});

function onBlur(field: string, value: string) {
  const safeValue = value?.trim() || 'empty';
  pushToDataLayer('blur', {
    category: 'Form Input',
    field,
    value: safeValue,
  });
}

const formStarted = ref(false);
function onFormFocus() {
  if (!formStarted.value) {
    formStarted.value = true;
    // 获取诊断事件参数（包含双归因和 UTM）
    const diagnosticParams = getDiagnosticEventParams();
    pushToDataLayer('form_start', {
      form_id: 'contact_us_form_mobile',
      form_name: 'Contact Us Mobile',
      form_start_time: new Date().toISOString(),
      ...diagnosticParams,
    });
  }
}

function openGoogleMap() {
  const address = '248 Guanghua Road, Minhang District, Shanghai';
  const roomInfo = 'Room 205, 2nd Floor';
  const query = encodeURIComponent(`${address} (${roomInfo})`);
  window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');

  pushToDataLayer('blur', {
    category: 'Form Input',
    field: 'openGoogleMap',
    value: 'clicked',
  });
}

function goToContactUs() {
  router.push('/contact-us');
  pushToDataLayer('click', {
    category: 'ContactUs',
    label: 'Contact Us Button',
  });
}

const hovered = ref(false);

const buttonStyle = computed(() => ({
  boxShadow: hovered.value ? "0px 0px 20px 0px #0D0971" : "none",
  transition: "box-shadow 0.3s ease",
}));

// Define the shape of our form data with TypeScript
interface FormData {
  firstName: string;
  email: string;
  companyName: string;
  lookingFor: string;
  captchaToken: string;
}

// Initialize form data with refs
const formData = ref<FormData>({
  firstName: "",
  email: "",
  companyName: "",
  lookingFor: "",
  captchaToken: "",
});

const requiredFields = [
  { key: 'firstName', label: 'Name', validationKey: 'nameRequired' },
  { key: 'email', label: 'Email', validationKey: 'emailRequired' },
  { key: 'companyName', label: 'Company', validationKey: 'companyRequired' },
  { key: 'lookingFor', label: 'Message', validationKey: 'messageRequired' },
];

// Form submission handler
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const submitForm = async () => {
  // 获取诊断事件参数（用于 form_error_* 事件）
  const diagnosticParams = getDiagnosticEventParams();

  // 必填项校验
  for (const field of requiredFields) {
    if (!formData.value[field.key]) {
      // 🔴 form_error_missingRequiredFields: 必填字段缺失
      pushToDataLayer('form_error_missingRequiredFields', {
        form_id: 'contact_us_form_mobile',
        form_name: 'Contact Us Mobile',
        error_message: `Required field missing: ${field.key}`,
        field_name: field.key,
        ...diagnosticParams,
      });
      ElMessage({
        message: contactTexts.value.validationMessages?.[field.validationKey] || `Please fill out the "${field.label}" field.`,
        type: "warning",
      });
      return;
    }
  }

  // 校验 Email 格式合法性
  if (!isValidEmail(formData.value.email)) {
    // 🔴 form_error_missingRequiredFields: Email 格式无效也属于必填字段问题
    pushToDataLayer('form_error_missingRequiredFields', {
      form_id: 'contact_us_form_mobile',
      form_name: 'Contact Us Mobile',
      error_message: 'Invalid email format',
      field_name: 'email',
      ...diagnosticParams,
    });
    ElMessage({
      message: contactTexts.value.validationMessages?.invalidEmail || 'Please enter a valid email address.',
      type: "warning",
    });
    return;
  }

  // reCAPTCHA 校验
  if (!recaptchaToken.value) {
    // 🔴 form_error_recaptchaNotCompleted: reCAPTCHA 未完成
    pushToDataLayer('form_error_recaptchaNotCompleted', {
      form_id: 'contact_us_form_mobile',
      form_name: 'Contact Us Mobile',
      error_message: 'Captcha not completed',
      field_name: 'captcha',
      ...diagnosticParams,
    });
    ElMessage({
      message: contactTexts.value.validationMessages?.captchaRequired || 'Please complete the reCAPTCHA verification.',
      type: "warning",
    });
    return;
  }

  // 获取 UTM 参数
  const utmParams = getUtmParams();

  const payload = {
    data: {
      FirstName: formData.value.firstName,
      Email: formData.value.email,
      CompanyName: formData.value.companyName,
      LookFor: formData.value.lookingFor,
      anonymous_id: getAnonymousId(),
      captchaToken: recaptchaToken.value,
      // UTM 参数 (hidden fields)
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_campaign_id: utmParams.utm_campaign_id,
      utm_adgroup: utmParams.utm_adgroup,
      utm_adgroup_id: utmParams.utm_adgroup_id,
      utm_ad: utmParams.utm_ad,
      utm_matchtype: utmParams.utm_matchtype,
      utm_device: utmParams.utm_device,
      utm_term: utmParams.utm_term,
      // URL Snapshot fields (hidden fields)
      first_page_url: getFirstPageUrl(),
      landing_page: getLandingPage(),
      // 时间 & 技术补充字段 (hidden fields)
      lead_submit_time: new Date().toISOString(),
      user_agent: navigator.userAgent,
    },
  };

  // 获取 CTA 信息（提前获取，用于 form_submit 和 form_submit_success）
  const ctaInfo = getCTAInfo();

  // 获取表单提交事件的完整参数（包含 lead_id, 双归因参数, UTM 等）
  const submitParams = getFormSubmitSuccessParams('contact_us_form_mobile', 'Contact Us Mobile');

  // 🔴 form_submit: 发送请求时触发（用户点击提交按钮）
  pushToDataLayer('form_submit', {
    ...submitParams,
    cta_location: ctaInfo?.cta_location || 'contact_page',
    cta_type: ctaInfo?.cta_type || 'direct',
  });

  try {
    const response = await fetch(`${API_BASE_URL}/api/contact-uses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error("Submission failed:", result);

      const errorMessage = result.error?.message || 'Unknown error';

      // 判断是否为 reCAPTCHA 验证失败
      const isRecaptchaError =
        errorMessage.toLowerCase().includes('recaptcha') ||
        errorMessage.toLowerCase().includes('captcha') ||
        errorMessage.toLowerCase().includes('g-recaptcha-response') ||
        errorMessage.toLowerCase().includes('expired token') ||
        errorMessage.toLowerCase().includes('invalid token') ||
        errorMessage.toLowerCase().includes('missing token') ||
        errorMessage.toLowerCase().includes('no recaptcha token');

      if (isRecaptchaError) {
        // 🔴 form_error_recaptchaVerificationFailed: reCAPTCHA 服务器验证失败
        pushToDataLayer('form_error_recaptchaVerificationFailed', {
          form_id: 'contact_us_form_mobile',
          form_name: 'Contact Us Mobile',
          error_message: `reCAPTCHA verification failed: ${errorMessage}`,
          field_name: 'captcha',
          http_status: response.status,
          ...diagnosticParams,
        });
      } else {
        // 🔴 form_error_httpRequestFailed: HTTP 错误（4xx/5xx）
        pushToDataLayer('form_error_httpRequestFailed', {
          form_id: 'contact_us_form_mobile',
          form_name: 'Contact Us Mobile',
          error_message: `HTTP request failed: ${response.status} - ${errorMessage}`,
          field_name: 'api_response',
          http_status: response.status,
          ...diagnosticParams,
        });
      }

      alert("Submission failed. Please try again.");
      return;
    }

    // 🔴 form_submit_success: 接口成功后触发
    pushToDataLayer('form_submit_success', {
      ...submitParams,
      cta_location: ctaInfo?.cta_location || 'contact_page',
      cta_type: ctaInfo?.cta_type || 'direct',
    });

    // 清除 CTA 信息
    clearCTAInfo();

    ElMessage({
      message: contactTexts.value.validationMessages?.submitSuccess || 'Thank you for reaching out. A Creston representative will be in touch shortly.',
      type: "success",
    });

    Object.assign(formData.value, {
      firstName: '',
      email: '',
      companyName: '',
      lookingFor: '',
      anonymous_id: '',
    });

    // 重置 captcha
    if (typeof grecaptcha !== 'undefined' && grecaptcha.reset) {
      grecaptcha.reset();
      recaptchaToken.value = '';
    }

  } catch (error) {
    console.error("Error submitting form:", error);

    // 🔴 form_error_httpRequestFailed: 网络错误或请求失败
    pushToDataLayer('form_error_httpRequestFailed', {
      form_id: 'contact_us_form_mobile',
      form_name: 'Contact Us Mobile',
      error_message: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      field_name: 'network',
      error_type: 'network_error',
      ...diagnosticParams,
    });

    ElMessage({
      message: contactTexts.value.validationMessages?.submitError || 'Submission failed. Please try again later.',
      type: "error",
    });
  }
};
</script>

<style scoped>
.mobile-contact-container {
  width: 100%;
  min-height: 100vh;
}
</style>
